import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import * as React from 'react';

import type { EditOtherInformationFormData } from '../../schemas/editOtherInformationPanel.schema';
import type {
  OtherInformationFormErrors,
  OtherInformationFormState,
  UseEditOtherInformationPanelProps,
  UseEditOtherInformationPanelReturn,
} from './useEditOtherInformationPanel.types';

import { editOtherInformationSchema } from '../../schemas/editOtherInformationPanel.schema';
import { useProfile } from '../use-profile/useProfile';
import { useUpdateProfileOtherInformation } from '../use-update-profile-other-information/useUpdateProfileOtherInformation';
import { buildInitialOtherInformationFormState } from './useEditOtherInformationPanel.utils';

export function useEditOtherInformationPanel({
  onClose,
}: UseEditOtherInformationPanelProps): UseEditOtherInformationPanelReturn {
  const { profile, isProfileLoading, isProfileError } = useProfile();
  const { mutate, isPending, isError, isSuccess } =
    useUpdateProfileOtherInformation();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const [isExitModalOpen, setIsExitModalOpen] = React.useState<boolean>(false);

  const [formState, setFormState] = React.useState<OtherInformationFormState>(
    () => buildInitialOtherInformationFormState(profile)
  );

  const [errors, setErrors] = React.useState<OtherInformationFormErrors>({});

  const initialized = React.useRef(false);
  const initialFormStateRef =
    React.useRef<OtherInformationFormState>(formState);
  const isPageRefreshingRef = React.useRef(false);
  const isDiscardingRef = React.useRef(false);

  React.useEffect(() => {
    const handleBeforeUnload = (): void => {
      isPageRefreshingRef.current = true;
    };
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    if (isSuccess) {
      emitFlagMessage('success', 'Changes saved successfully!', 5000);
      onClose();
    }
  }, [isSuccess, onClose]);

  React.useEffect(() => {
    if (profile && !initialized.current) {
      initialized.current = true;
      const initial = buildInitialOtherInformationFormState(profile);
      initialFormStateRef.current = initial;
      setFormState(initial);
    }
  }, [profile]);

  const handleFieldChange = React.useCallback(
    (field: keyof OtherInformationFormState, value: string): void => {
      setFormState(prev => ({ ...prev, [field]: value }));
      setErrors(prev => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const validate =
    React.useCallback((): EditOtherInformationFormData | null => {
      const result = editOtherInformationSchema.safeParse(formState);
      if (result.success) {
        setErrors({});
        return result.data;
      }
      const nextErrors: OtherInformationFormErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof OtherInformationFormErrors;
        if (!nextErrors[field]) {
          nextErrors[field] = issue.message;
        }
      }
      setErrors(nextErrors);
      return null;
    }, [formState]);

  const isDirty =
    !isSuccess && formState.nickName !== initialFormStateRef.current.nickName;

  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  useNavigationBlock(
    React.useCallback(
      (): boolean => isDirtyRef.current && !isDiscardingRef.current,
      []
    )
  );

  const handleSubmit = React.useCallback((): void => {
    if (!isDirtyRef.current || !profile) return;
    const sanitized = validate();
    if (!sanitized) return;
    mutate({
      nickName: sanitized.nickName || null,
    });
  }, [validate, profile, mutate]);

  const handleCloseAttempt = React.useCallback((): void => {
    if (isPageRefreshingRef.current) return;
    if (isPending) return;
    if (isDirtyRef.current) {
      setIsExitModalOpen(true);
    } else {
      onClose();
    }
  }, [isPending, onClose]);

  const handleExit = React.useCallback((): void => {
    isDiscardingRef.current = true;
    setIsExitModalOpen(false);
    onClose();
  }, [onClose]);

  const handleKeepEditing = React.useCallback((): void => {
    setIsExitModalOpen(false);
  }, []);

  const isProfileReady = !!profile;

  return {
    formState,
    errors,
    isMobile,
    isExitModalOpen,
    isDirty,
    isPending,
    isError,
    isProfileLoading,
    isProfileError,
    isProfileReady,
    handleFieldChange,
    handleSubmit,
    handleCloseAttempt,
    handleExit,
    handleKeepEditing,
  };
}
